// Data Contracts
import {
  JudgeReviewFiling,
  JudgeReviewFilingConfiguration,
} from '@data-contracts';

// Local
import {
  JudgeReviewConfirmCase,
  JudgeReviewConfirmHeader,
  JudgeReviewConfirmOutcome,
  JudgeReviewConfirmRecipient,
} from '../models/judge-review-confirm.view-model';
import { resolveJudicialActionName } from './judge-review-config.utils';
import { mapServedRecipients } from './map-judge-review-recipients.utils';
import { personDisplayName } from './person-display-name.util';
import { actionDocuments } from './judge-review-action.utils';

/**
 * Pure mappers from the judge-review `@data-contracts` DTOs to the Confirmation
 * page UI models. This is the single translation boundary for the page's
 * display bindings — the component calls these, the template consumes UI models
 * only. No Angular dependencies.
 */

/**
 * `JRF008 DocumentReviewIncomplete` — the API's submit gate for the
 * multi-document review flow ("every document must be reviewed before the filing
 * can be submitted"). The single definition of this code for the feature: the
 * data service also imports it to keep it out of the studio's warning list.
 */
export const DOCUMENT_REVIEW_INCOMPLETE_CODE = 'JRF008';

/** Maps the filing into the confirmation header / context UI model. */
export function mapConfirmHeader(
  filing: JudgeReviewFiling | null | undefined,
): JudgeReviewConfirmHeader | null {
  if (!filing) {
    return null;
  }
  return {
    reviewerName: personDisplayName(filing.judgeReviewerName),
    courtName: filing.courtName ?? '',
  };
}

/** Maps the lead case into the Case Information UI model. */
export function mapConfirmCase(
  filing: JudgeReviewFiling | null | undefined,
): JudgeReviewConfirmCase | null {
  const firstCase = filing?.judgeReviewCases?.[0];
  if (!firstCase) {
    return null;
  }
  return {
    caseNumber: firstCase.caseNumber ?? '',
    caseName: firstCase.caseName ?? '',
    caseClass: firstCase.caseClass ?? '',
  };
}

/**
 * Maps the distribution the judge chose into the Confirmation page's recipient
 * rows. The rules live in the shared `mapServedRecipients`.
 */
export function mapConfirmRecipients(
  filing: JudgeReviewFiling | null | undefined,
  config: JudgeReviewFilingConfiguration | null | undefined,
): JudgeReviewConfirmRecipient[] {
  return mapServedRecipients(filing, config);
}

/**
 * Maps the filing (+ configuration) into the Confirmation page's
 * disposition / document / validation outcome UI model. This keeps the
 * disposition, document-title, filing-type and validation derivations in one
 * mapper instead of inline in the component. Returns null while the filing is
 * loading.
 *
 * Serve vs status-update-only is deliberately not derived here: the API routes
 * submit solely on the judge's "update without serving" choice and the filing's
 * eligibility, so the Confirmation page reads that live state instead.
 */
export function mapConfirmOutcome(
  filing: JudgeReviewFiling | null | undefined,
  config: JudgeReviewFilingConfiguration | null | undefined,
): JudgeReviewConfirmOutcome | null {
  if (!filing) {
    return null;
  }

  const documents = actionDocuments(filing);
  const primaryDoc = documents[0];

  const disposition = primaryDoc
    ? resolveJudicialActionName(
        config,
        primaryDoc.existingDocumentId,
        primaryDoc.selectedJudicialActionId,
      )
    : null;

  return {
    disposition,
    // `||` (not `??`) so an empty-string title falls back to the filing type,
    // matching the sibling detail mapper.
    documentTitle:
      primaryDoc?.documentTitle || primaryDoc?.judgeReviewFilingType || null,
    outgoingFilingType: primaryDoc?.judgeReviewFilingType ?? '',
    validationErrors: (filing.validationErrors ?? [])
      // The API's submit completes the review of the last outstanding document
      // itself, so `JRF008` is not a blocker while exactly one remains. With more
      // than one outstanding the judge skipped a document, and it still blocks.
      .filter(
        error =>
          error.code !== DOCUMENT_REVIEW_INCOMPLETE_CODE ||
          documents.filter(document => !document.isReviewed).length !== 1,
      )
      .map(error => error.description)
      .filter((description): description is string => !!description),
  };
}
